import React, { useEffect, useState, useRef } from "react";
import BaseModal from "components/BaseModal";
import { useSession } from "hooks/useSession";
import { showMainToast } from "utils/showToast";
import { fetchWrapper } from "utils/apiFetch";

// ─────────────────────────────────────────────────────────────────────────────
// Searchable Select — zero external deps, works with Bootstrap classes
// ─────────────────────────────────────────────────────────────────────────────
function SearchableSelect({ options = [], value, onChange, placeholder = "Search and select…", disabled = false }) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState("");
  const ref               = useRef(null);
  const inputRef          = useRef(null);

  const filtered = options.filter(
    (o) =>
      o.name.toLowerCase().includes(query.toLowerCase()) ||
      String(o.phone || "").includes(query)
  );

  const selected = options.find((o) => String(o.id) === String(value));

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const handleToggle = () => {
    if (!disabled) setOpen((p) => !p);
  };

  const handleSelect = (id) => {
    onChange(String(id));
    setOpen(false);
    setQuery("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange("");
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {/* Trigger button */}
      <div
        className={`form-control d-flex justify-content-between align-items-center ${disabled ? "bg-light text-muted" : ""}`}
        style={{
          cursor: disabled ? "not-allowed" : "pointer",
          minHeight: 38,
          userSelect: "none",
        }}
        onClick={handleToggle}
      >
        <span className={selected ? "text-dark" : "text-muted"} style={{ fontSize: 14 }}>
          {selected ? (
            <>
              <strong>{selected.name}</strong>
              {selected.phone && (
                <span className="text-muted ms-2" style={{ fontSize: 12 }}>
                  {selected.phone}
                </span>
              )}
            </>
          ) : (
            placeholder
          )}
        </span>
        <div className="d-flex align-items-center gap-1">
          {selected && !disabled && (
            <span
              onClick={handleClear}
              className="text-muted"
              style={{ fontSize: 16, lineHeight: 1, cursor: "pointer" }}
              title="Clear"
            >
              &times;
            </span>
          )}
          <i className={`bi bi-chevron-${open ? "up" : "down"} text-muted`} style={{ fontSize: 12 }} />
        </div>
      </div>

      {/* Dropdown panel */}
      {open && (
        <div
          className="border rounded bg-white shadow"
          style={{
            position: "absolute",
            zIndex: 1060,
            width: "100%",
            maxHeight: 240,
            overflowY: "auto",
            top: "calc(100% + 4px)",
          }}
        >
          {/* Search input */}
          <div
            className="p-2 border-bottom bg-white"
            style={{ position: "sticky", top: 0, zIndex: 1 }}
          >
            <input
              ref={inputRef}
              className="form-control form-control-sm"
              placeholder="Type name or phone…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* None option */}
          <div
            className="px-3 py-2 text-muted small border-bottom"
            style={{ cursor: "pointer" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f8f9fa")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "")}
            onClick={() => handleSelect("")}
          >
            — None / Clear —
          </div>

          {/* Results */}
          {filtered.length === 0 ? (
            <div className="px-3 py-3 text-muted text-center small">
              <i className="bi bi-search me-1" />
              No results found
            </div>
          ) : (
            filtered.map((o) => {
              const isActive = String(o.id) === String(value);
              return (
                <div
                  key={o.id}
                  className={`px-3 py-2 d-flex justify-content-between align-items-center ${isActive ? "bg-primary text-white" : ""}`}
                  style={{ cursor: "pointer", transition: "background 0.1s" }}
                  onMouseEnter={(e) => {
                    if (!isActive) e.currentTarget.style.background = "#eef2ff";
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) e.currentTarget.style.background = "";
                  }}
                  onClick={() => handleSelect(o.id)}
                >
                  <div>
                    <div className="fw-semibold" style={{ fontSize: 14, color:'blue' }}>{o.name}</div>
                    {o.phone && (
                      <div style={{ fontSize: 12, opacity: 0.75,color:'blue'  }}>{o.phone}</div>
                    )}
                  </div>
                  {isActive && <i className="bi bi-check2 fw-bold" />}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Role badge helper
// ─────────────────────────────────────────────────────────────────────────────
const ROLE_COLORS = {
  "White label":        "warning",
  "Master Distributor": "info",
  "Distributer":        "success",
  "Retailer":           "secondary",
};

function RoleBadge({ role }) {
  const color = ROLE_COLORS[role] || "secondary";
  return (
    <span className={`badge bg-${color} text-dark`} style={{ fontSize: 11 }}>
      {role}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Field row with icon label
// ─────────────────────────────────────────────────────────────────────────────
function FieldRow({ icon, label, color = "primary", children }) {
  return (
    <div className="col-md-12">
      <label className="form-label d-flex align-items-center gap-2 mb-1" style={{ fontWeight: 600, fontSize: 13 }}>
        <span
          className={`d-inline-flex align-items-center justify-content-center rounded-circle bg-${color} bg-opacity-10`}
          style={{ width: 24, height: 24 }}
        >
          <i className={`bi bi-${icon} text-${color}`} style={{ fontSize: 12 }} />
        </span>
        {label}
      </label>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Modal
// ─────────────────────────────────────────────────────────────────────────────
export default function AssignHierarchyModal({ show, onClose, onSuccess }) {
  const { userid } = useSession();
  const loggedRole = localStorage.getItem("role") || "";

  // Member fields
  const [name,  setName]  = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [roleId, setRoleId] = useState("5");

  // Hierarchy selections
  const [whiteLabelId,  setWhiteLabelId]  = useState("");
  const [masterDistId,  setMasterDistId]  = useState("");
  const [distributorId, setDistributorId] = useState("");

  // Options from API
  const [options,   setOptions]   = useState({});
  const [fetching,  setFetching]  = useState(false);
  const [loading,   setLoading]   = useState(false);

  // Validation errors
  const [errors, setErrors] = useState({});

  // ── Determine which hierarchy fields the logged-in role can set ───────────
  const showWhiteLabel  = ["Main Admin", "Admin"].includes(loggedRole);
  const showMasterDist  = ["Main Admin", "Admin", "White label"].includes(loggedRole);
  const showDistributor = ["Main Admin", "Admin", "White label", "Master Distributor"].includes(loggedRole);

  // ── Role options visible per logged-in role ───────────────────────────────
  const roleOptions = (() => {
    const all = [
      { value: "5", label: "Retailer" },
      { value: "4", label: "Distributor" },
      { value: "3", label: "Super Distributor" },
      { value: "2", label: "White label" },
    ];
    if (["Main Admin", "Admin"].includes(loggedRole)) return all;
    if (loggedRole === "White label")        return all.filter((r) => ["5","4","3"].includes(r.value));
    if (loggedRole === "Master Distributor") return all.filter((r) => ["5","4"].includes(r.value));
    return all.filter((r) => r.value === "5");
  })();

  // ── Load hierarchy options when modal opens ───────────────────────────────
  useEffect(() => {
    if (!show) return;

    // Reset form
    setName(""); setEmail(""); setPhone(""); setRoleId("5");
    setWhiteLabelId(""); setMasterDistId(""); setDistributorId("");
    setErrors({});

    setFetching(true);
    const fd = new FormData();
    fd.append("action",     "AssignHierarchy");
    fd.append("sub_action", "GET_OPTIONS");
    fd.append("member_id",     userid);

    fd.append("userid",     userid);    
    fetchWrapper("https://api.mokshiq.in/retailer_action", {
      method: "POST",
     
      body: fd,
    })
      
      .then((res) => {
        if (res.status) {
          setOptions(res.data.options || {});
        } else {
          showMainToast(res.data || "Failed to load options", "danger");
        }
      })
      .catch(() => showMainToast("Network error loading options", "danger"))
      .finally(() => setFetching(false));
  }, [show]);

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!name.trim())  errs.name  = "Name is required";
    if (!email.trim()) errs.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(email)) errs.email = "Invalid email format";
    if (!phone.trim()) errs.phone = "Phone is required";
    else if (!/^\d{10,15}$/.test(phone.trim())) errs.phone = "Enter a valid phone number";
    if (!roleId) errs.role = "Role is required";
    return errs;
  };

  // ── Save (Create account + assign hierarchy) ──────────────────────────────
  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setLoading(true);

    const fd = new FormData();
    fd.append("action",     "AssignHierarchy");
    fd.append("sub_action", "ASSIGN");
    fd.append("userid",     userid);
    fd.append("name",       name.trim());
    fd.append("email",      email.trim());
    fd.append("phone",      phone.trim());
    fd.append("role",       roleId);
      fd.append("member_id",     userid);
    if (whiteLabelId)  fd.append("white_label_id",  whiteLabelId);
    if (masterDistId)  fd.append("master_dist_id",  masterDistId);
    if (distributorId) fd.append("distributor_id",  distributorId);

    try {
      const data  = await fetchWrapper("https://api.mokshiq.in/retailer_action", {
        method: "POST",
        
        body: fd,
      });
     

      if (data.status) {
        showMainToast(data.data || "Member created successfully!", "success");
        onClose();
        if (onSuccess) onSuccess();
      } else {
        showMainToast(data.data || "Something went wrong!", "danger");
      }
    } catch {
      showMainToast("Network error. Please try again.", "danger");
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <BaseModal
      show={show}
      onClose={onClose}
      title="Add Member with Hierarchy"
      footerButtons={[
        { label: "Cancel", className: "btn-secondary", dismiss: true },
        {
          label: loading ? "Creating…" : "Create & Assign",
          className: "btn-primary",
          onClick: handleSave,
          disabled: loading,
        },
      ]}
    >
      <div className="card-body px-1">
        {fetching ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
            <div className="mt-3 text-muted small">Loading hierarchy options…</div>
          </div>
        ) : (
          <div>
            {/* ── Section 1: Member Details ── */}
            <div className="mb-3">
              <div
                className="d-flex align-items-center gap-2 mb-3 pb-2"
                style={{ borderBottom: "2px solid #e9ecef" }}
              >
                <span
                  className="d-inline-flex align-items-center justify-content-center rounded bg-primary text-white"
                  style={{ width: 28, height: 28 }}
                >
                  <i className="bi bi-person-plus-fill" style={{ fontSize: 13 }} />
                </span>
                <span className="fw-bold" style={{ fontSize: 14, color: "#1a1a2e" }}>
                  Member Details
                </span>
              </div>

              <div className="row g-3">
                {/* Name */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    Full Name <span className="text-danger">*</span>
                  </label>
                  <input
                    className={`form-control ${errors.name ? "is-invalid" : ""}`}
                    id="h-name"
                    type="text"
                    placeholder="Enter full name"
                    value={name}
                    onChange={(e) => { setName(e.target.value); setErrors((p) => ({ ...p, name: "" })); }}
                  />
                  {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                </div>

                {/* Email */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    Email <span className="text-danger">*</span>
                  </label>
                  <input
                    className={`form-control ${errors.email ? "is-invalid" : ""}`}
                    id="h-email"
                    type="email"
                    placeholder="Enter email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: "" })); }}
                  />
                  {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                </div>

                {/* Phone */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    Phone <span className="text-danger">*</span>
                  </label>
                  <input
                    className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                    id="h-phone"
                    type="text"
                    placeholder="Enter phone number"
                    value={phone}
                    onChange={(e) => { setPhone(e.target.value); setErrors((p) => ({ ...p, phone: "" })); }}
                  />
                  {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                </div>

                {/* Role */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    Role <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${errors.role ? "is-invalid" : ""}`}
                    value={roleId}
                    onChange={(e) => { setRoleId(e.target.value); setErrors((p) => ({ ...p, role: "" })); }}
                  >
                    {roleOptions.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                  {errors.role && <div className="invalid-feedback">{errors.role}</div>}
                </div>
              </div>
            </div>

            {/* ── Section 2: Hierarchy Assignment ── */}
            {(showWhiteLabel || showMasterDist || showDistributor) && (
              <div>
                <div
                  className="d-flex align-items-center gap-2 mb-3 pb-2"
                  style={{ borderBottom: "2px solid #e9ecef" }}
                >
                  <span
                    className="d-inline-flex align-items-center justify-content-center rounded bg-warning text-dark"
                    style={{ width: 28, height: 28 }}
                  >
                    <i className="bi bi-diagram-3-fill" style={{ fontSize: 13 }} />
                  </span>
                  <span className="fw-bold" style={{ fontSize: 14, color: "#1a1a2e" }}>
                    Assign Hierarchy
                  </span>
                  <span className="badge bg-light text-muted ms-1" style={{ fontSize: 11 }}>
                    Optional
                  </span>
                </div>

                <div className="row g-3">
                  {/* White Label */}
                  {showWhiteLabel && options.white_labels && (
                    <FieldRow icon="star-fill" label="White Label" color="warning">
                      <SearchableSelect
                        options={options.white_labels || []}
                        value={whiteLabelId}
                        onChange={setWhiteLabelId}
                        placeholder="Select White Label…"
                      />
                    </FieldRow>
                  )}

                  {/* Master Distributor */}
                  {showMasterDist && options.master_distributors && (
                    <FieldRow icon="diagram-3" label="Master Distributor" color="info">
                      <SearchableSelect
                        options={options.master_distributors || []}
                        value={masterDistId}
                        onChange={setMasterDistId}
                        placeholder="Select Master Distributor…"
                      />
                    </FieldRow>
                  )}

                  {/* Distributor */}
                  {showDistributor && options.distributors && (
                    <FieldRow icon="person-badge" label="Distributor" color="success">
                      <SearchableSelect
                        options={options.distributors || []}
                        value={distributorId}
                        onChange={setDistributorId}
                        placeholder="Select Distributor…"
                      />
                    </FieldRow>
                  )}
                </div>

                {/* Info note */}
                <div
                  className="mt-3 p-2 rounded d-flex gap-2 align-items-start"
                  style={{ background: "#f0f7ff", border: "1px solid #cce4ff", fontSize: 12 }}
                >
                  <i className="bi bi-info-circle-fill text-primary mt-1" style={{ flexShrink: 0 }} />
                  <span className="text-muted">
                    Hierarchy fields are optional. Only accounts active and within your network are listed.
                    Login credentials will be sent to the member after creation.
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </BaseModal>
  );
}